/**
 * Data-quality rule library, assessable datasets and dataset generation shapes.
 *
 * PLACEHOLDER / ILLUSTRATIVE DATA. Classified INTERNAL.
 *
 * The three defect profiles are tuned so the demo files tell three different
 * stories: one is clean enough to flag, one must be quarantined, one has to be
 * blocked. A rule library that always returns "pass" teaches nothing.
 */

import type {
  DatasetDefectProfile,
  DatasetShape,
  DqsAgentSummary,
  DqsDataset,
  DqsScope,
  Id,
  QaFamily,
  QaRule,
} from '../../contracts';

export const QA_FAMILIES: readonly QaFamily[] = [
  {
    id: 'integrity',
    name: 'Integrity',
    desc: 'Structure, types, keys and duplicates',
    pillar: 'commerce',
  },
  {
    id: 'completeness',
    name: 'Completeness',
    desc: 'Required fields present, gaps distinguished from zeros',
    pillar: 'people',
  },
  {
    id: 'plausibility',
    name: 'Plausibility',
    desc: 'Ranges, outliers, distribution and continuity',
    pillar: 'audience',
  },
  {
    id: 'provenance',
    name: 'Provenance',
    desc: 'Source declared, vintage, method disclosed',
    pillar: 'brand',
  },
  {
    id: 'fitness',
    name: 'Model-fitness',
    desc: 'Variance, granularity and alignment for modelling',
    pillar: 'operations',
  },
];

export const QA_RULES: readonly QaRule[] = [
  {
    id: 'r-schema',
    family: 'integrity',
    name: 'Schema conforms to the declared contract',
    sev: 'critical',
    action: 'block',
  },
  {
    id: 'r-types',
    family: 'integrity',
    name: 'Column types parse without coercion',
    sev: 'critical',
    action: 'block',
  },
  {
    id: 'r-keys',
    family: 'integrity',
    name: 'Composite key is unique',
    sev: 'critical',
    action: 'quarantine',
  },
  {
    id: 'r-dupes',
    family: 'integrity',
    name: 'No duplicate rows',
    sev: 'serious',
    action: 'quarantine',
  },
  {
    id: 'r-required',
    family: 'completeness',
    name: 'Required fields populated',
    sev: 'serious',
    action: 'quarantine',
  },
  {
    id: 'r-nullzero',
    family: 'completeness',
    name: 'Missing is not encoded as zero',
    sev: 'serious',
    action: 'flag',
  },
  {
    id: 'r-weeks',
    family: 'completeness',
    name: 'Every week present per partner and country',
    sev: 'warning',
    action: 'flag',
  },
  {
    id: 'r-coverage',
    family: 'completeness',
    name: 'Channel coverage above the agreed floor',
    sev: 'warning',
    action: 'flag',
  },
  {
    id: 'r-range',
    family: 'plausibility',
    name: 'Values inside plausible range',
    sev: 'serious',
    action: 'quarantine',
  },
  {
    id: 'r-outlier',
    family: 'plausibility',
    name: 'No extreme outliers beyond 4 sigma',
    sev: 'warning',
    action: 'flag',
  },
  {
    id: 'r-jump',
    family: 'plausibility',
    name: 'No implausible week-on-week jumps',
    sev: 'warning',
    action: 'flag',
  },
  {
    id: 'r-negative',
    family: 'plausibility',
    name: 'No negative volumes',
    sev: 'critical',
    action: 'block',
  },
  {
    id: 'r-source',
    family: 'provenance',
    name: 'Source declared for every metric',
    sev: 'serious',
    action: 'flag',
  },
  {
    id: 'r-vintage',
    family: 'provenance',
    name: 'Data vintage within the agreed window',
    sev: 'warning',
    action: 'flag',
  },
  {
    id: 'r-method',
    family: 'provenance',
    name: 'Estimation method disclosed where used',
    sev: 'warning',
    action: 'flag',
    canRun: false,
  },
  {
    id: 'r-variance',
    family: 'fitness',
    name: 'Sufficient variance to model',
    sev: 'serious',
    action: 'flag',
  },
  {
    id: 'r-grain',
    family: 'fitness',
    name: 'Granularity matches the model grain',
    sev: 'critical',
    action: 'block',
  },
  {
    id: 'r-align',
    family: 'fitness',
    name: 'Weeks aligned to the declared week start',
    sev: 'serious',
    action: 'quarantine',
  },
  {
    id: 'r-collin',
    family: 'fitness',
    name: 'No perfect collinearity between variables',
    sev: 'warning',
    action: 'flag',
    canRun: false,
  },
];

export const DQS_SCOPES: readonly DqsScope[] = [
  { id: 'mmm', label: 'MMM Variable Creation', enabled: true },
  { id: 'proi', label: 'Business Case', enabled: false },
  { id: 'media', label: 'Media Impact Forecast', enabled: false },
  { id: 'activation', label: 'Activation Report', enabled: false },
  { id: 'strategic', label: 'Strategic Relevance', enabled: false },
  { id: 'market', label: 'Market Value', enabled: false },
];

export const DQS_DATASETS: readonly DqsDataset[] = [
  {
    id: 'mmm-cbi',
    title: 'Category Portfolio MMM Phase 1',
    brand: 'Constellation Brands',
    partner: 'Advisory Programme',
    period: 'Jan 2024 — Dec 2026',
    confidence: 97,
    stats: [
      ['Partners', 45],
      ['Touchpoints', 50],
      ['Metrics', 3],
      ['Candidates', 3],
    ],
    lastModified: 'Mar 15, 2026',
  },
  {
    id: 'mmm-adidas',
    title: 'Adidas Sponsorship Portfolio MMM Dataset 2022—2023',
    brand: 'Adidas',
    partner: 'Multi-Partner Portfolio',
    period: 'Jan 2022 — Dec 2023',
    confidence: 96,
    stats: [
      ['Total rows', 5408],
      ['Partners', 8],
      ['Coverage %', 96.8],
      ['Variables', 3],
    ],
    lastModified: 'Mar 10, 2026',
  },
  {
    id: 'mmm-allianz',
    title: 'Allianz Football Sponsorships MMM Build',
    brand: 'Allianz',
    partner: 'Football Portfolio',
    period: 'Jan 2023 — Dec 2025',
    confidence: 94,
    stats: [
      ['Total rows', 3124],
      ['Partners', 3],
      ['Coverage %', 92.4],
      ['Variables', 2],
    ],
    lastModified: 'Mar 12, 2026',
  },
];

export const DQS_AGENTS: readonly DqsAgentSummary[] = [
  {
    id: 'data-qa-agent',
    name: 'Data Quality Assurance — Dataset',
    ref: 'PNET_DataQA-Assessment',
    tags: ['Integrity', 'Completeness', 'Plausibility'],
    desc: 'Checks a delivered dataset before anyone builds on it',
  },
  {
    id: 'data-qa-measurement',
    name: 'Data Confidence Score',
    ref: 'dqs-scorecard',
    tags: ['Measurement', 'MMM', 'Vendor pricing'],
    desc: 'How much a figure can be trusted for the decision it has to carry',
  },
  {
    id: 'data-qa-delivery',
    name: 'Data Quality Assurance — Handover gate',
    ref: 'PNET_DataQA-Assessment',
    tags: ['Delivery', 'Provenance', 'Model-fitness'],
    desc: 'Checks a delivery file at the handover gate',
  },
];

/**
 * The Allianz shape doubles as the fallback for an unknown dataset id, so it is
 * bound to a name rather than reached through an index — the adapter can then
 * default without a non-null assertion.
 */
const ALLIANZ_SHAPE: DatasetShape = {
  partners: ['UEFA Champions League', 'FC Bayern Munich', 'Manchester United'],
  countries: ['Germany', 'UK', 'France', 'Italy'],
  weeks: 52,
  vintageMonths: 9,
  variables: [
    { id: 'ooh_exposure', metric: 'impressions', source: 'venue-data', base: 2.4e6 },
    { id: 'social_exposure', metric: 'engagements', source: 'social-listening', base: 1.2e5 },
  ],
};

const ALLIANZ_PROFILE: DatasetDefectProfile = {
  neg: 0,
  nonNum: 0,
  zeros: 0.01,
  outlier: 0.006,
  dupRow: 0.0025,
  dupKey: 0.0015,
  missReq: 0,
  misalign: 0.002,
  gapMod: 9,
};

/** Generation shape per dataset id. */
export const DATASET_SHAPES: Readonly<Record<Id, DatasetShape>> = {
  'mmm-allianz': ALLIANZ_SHAPE,
  'mmm-adidas': {
    partners: [
      'FIFA World Cup',
      'UEFA Champions League',
      'Formula 1',
      'NBA',
      'Premier League',
      'Marathon Majors',
      'Olympic Programme',
      'Tennis Grand Slams',
    ],
    countries: ['USA', 'Germany', 'UK', 'China', 'Japan', 'Brazil'],
    weeks: 52,
    // Past the 18-month Standard window on purpose, so the vintage check fires.
    vintageMonths: 31,
    variables: [
      { id: 'ooh_exposure', metric: 'impressions', source: 'venue-data', base: 3.1e6 },
      { id: 'social_exposure', metric: 'engagements', source: 'social-listening', base: 2.4e5 },
      // Deliberately sourceless, so the provenance check has something to find.
      { id: 'search_exposure', metric: 'search-volume', source: '', base: 1.8e5 },
    ],
  },
  'mmm-cbi': {
    partners: ['Programme Portfolio A', 'Programme Portfolio B', 'Programme Portfolio C'],
    countries: ['USA', 'Mexico', 'Canada'],
    weeks: 52,
    vintageMonths: 4,
    variables: [
      { id: 'ooh_exposure', metric: 'impressions', source: 'venue-data', base: 1.6e6 },
      { id: 'social_exposure', metric: 'engagements', source: 'social-listening', base: 0.9e5 },
      { id: 'search_exposure', metric: 'search-volume', source: 'search-trends', base: 1.1e5 },
    ],
  },
};

/** Defect injection rates. Each file has its own failure signature. */
export const DEFECT_PROFILES: Readonly<Record<Id, DatasetDefectProfile>> = {
  'mmm-allianz': ALLIANZ_PROFILE,
  // Negatives and non-numeric cells make this the file that must be BLOCKED.
  'mmm-adidas': {
    neg: 0.004,
    nonNum: 0.006,
    zeros: 0.012,
    outlier: 0.01,
    dupRow: 0.003,
    dupKey: 0.002,
    missReq: 0.003,
    misalign: 0.004,
    gapMod: 6,
  },
  // Missing dimensions and grain misalignment: quarantine, not block.
  'mmm-cbi': {
    neg: 0,
    nonNum: 0,
    zeros: 0.008,
    outlier: 0.007,
    dupRow: 0.004,
    dupKey: 0.004,
    missReq: 0.005,
    misalign: 0.01,
    gapMod: 5,
  },
};

/** Fallback shape and profile for an unrecognised dataset id. */
export const DEFAULT_SHAPE = ALLIANZ_SHAPE;
export const DEFAULT_PROFILE = ALLIANZ_PROFILE;
